import { useRef, useState } from 'react';
import { Dialog } from '../../design-system';
import './Sheet.css';

/** Drag distance that commits the dismiss instead of springing back. */
const DISMISS_THRESHOLD = 72;

/**
 * Bottom sheet — the phone shell's disclosure surface for anything the desktop
 * put behind hover or a header control.
 *
 * A sheet has exactly one exit: drag it down, or tap the scrim. It deliberately
 * carries no close button, so the gesture that opened it is the gesture that
 * closes it (DESIGN.md §6, double-close drift).
 *
 * Composed on Dialog so focus trap, background inert, Escape and scroll lock
 * stay owned in one place.
 */
const Sheet = ({
  isOpen,
  onClose,
  title,
  ariaLabel,
  children,
  className = '',
  /** Tall sheets scroll internally; short ones hug their content. */
  scrollable = false,
}) => {
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartRef = useRef(null);

  // Pointer events rather than touch: the same grip has to answer to a finger,
  // a trackpad and a stylus, and the phone shell renders on desktop widths too.
  const handlePointerDown = (event) => {
    if (!event.isPrimary || event.button !== 0) return;
    // Capture first, so a capture that does not take never leaves a drag armed.
    // It keeps a drag that wanders off the grip reporting, and still ends here
    // when the pointer is released somewhere else.
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartRef.current = event.clientY;
  };

  const handlePointerMove = (event) => {
    if (dragStartRef.current === null) return;
    // Downward only: dragging up must not stretch the sheet past its anchor.
    setDragOffset(Math.max(0, event.clientY - dragStartRef.current));
  };

  const endDrag = (event) => {
    const startY = dragStartRef.current;
    if (startY === null) return;
    dragStartRef.current = null;
    setDragOffset(0);
    // The release position decides, not the last rendered offset — a flick can
    // outrun a render, and a cancelled pointer has no position worth reading.
    if (event.type === 'pointerup' && event.clientY - startY >= DISMISS_THRESHOLD) {
      onClose?.();
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={ariaLabel || title}
      showClose={false}
      animated={false}
      overlayClassName="mobile-sheet-scrim"
      className={`mobile-sheet ${className}`.trim()}
    >
      <div
        className={`mobile-sheet__panel ${scrollable ? 'mobile-sheet__panel--scrollable' : ''}`.trim()}
        style={dragOffset ? { transform: `translateY(${dragOffset}px)`, transition: 'none' } : undefined}
      >
        <div
          className="mobile-sheet__grip-zone"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <div className="mobile-sheet__grip" aria-hidden="true" />
          {title && <h2 className="mobile-sheet__title">{title}</h2>}
        </div>
        <div className="mobile-sheet__body">{children}</div>
      </div>
    </Dialog>
  );
};

export default Sheet;
